import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, DocumentArrowDownIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, XMarkIcon, ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import api from '../utils/api';
import { qrMatchedNotice, qrMismatchLabels, qrStatusLabels } from './qrPresentation';

// Configurar el worker de PDF.js con una versión específica compatible
GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.js';

const normalizeText = (text) => {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ');
};

const getMatrizadorKey = (value) => normalizeText(value || 'SIN MATRIZADOR');

const canonicalDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const slash = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slash) return `${slash[3]}-${slash[2]}-${slash[1]}`;
  return '';
};

const isISODate = (value) => {
  const match = canonicalDate(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultAuditDateRange = () => {
  const today = new Date();
  const dateTo = formatLocalDate(today);
  const rollingStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  rollingStart.setDate(rollingStart.getDate() - 365);
  const rollingStartDate = formatLocalDate(rollingStart);
  const dateFrom = rollingStartDate > '2026-02-01' ? rollingStartDate : '2026-02-01';
  return { dateFrom, dateTo };
};

const parseMoneyToCents = (value) => {
  const match = String(value ?? '').trim().match(/^([+-]?)(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) return null;

  const sign = match[1] === '-' ? -1n : 1n;
  const fraction = (match[3] || '').padEnd(2, '0');
  return sign * ((window.BigInt(match[2]) * 100n) + window.BigInt(fraction || '0'));
};

const formatMoneyCents = (cents) => {
  const sign = cents < 0n ? '-' : '';
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
};

const getTramiteAmountCents = (tramite) => {
  const adjusted = parseMoneyToCents(tramite?.total_ajustado);
  if (adjusted !== null && adjusted > 0n) return adjusted;
  return parseMoneyToCents(tramite?.total) ?? 0n;
};

const extractComprobantesList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.comprobantes)) return payload.comprobantes;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const receiptFamily = (comprobante) => (
  Array.isArray(comprobante?.family) && comprobante.family.length > 0
    ? comprobante.family
    : [comprobante]
);

const currentQrCheck = (receipt) => (
  Array.isArray(receipt?.qr_checks) && receipt.qr_checks.length > 0
    ? receipt.qr_checks[0]
    : null
);

const individualReceiptAmount = (receipt) => {
  const check = currentQrCheck(receipt);
  const value = check?.manual_amount;
  return value == null ? null : parseMoneyToCents(value);
};

const displayQrStatus = (receipt) => {
  const check = currentQrCheck(receipt);
  if (!receipt?.qr_verification_required && !check) return qrStatusLabels.legacy;
  if (check?.status === 'manual_review_required' || check?.manual_review_required) return qrStatusLabels.manual_review_required;
  if (check?.status === 'qr_fields_matched') return qrStatusLabels.qr_fields_matched;
  return qrStatusLabels.pending;
};

const extractDaysFromComprobantes = (rows) => {
  const values = new Set();
  (Array.isArray(rows) ? rows : []).forEach((comprobante) => {
    const tramites = Array.isArray(comprobante?.tramites) ? comprobante.tramites : [];
    tramites.forEach((tramite) => {
      const day = canonicalDate(tramite?.fecha_documento);
      if (isISODate(day)) values.add(day);
    });
    if (!tramites.length) {
      const depositDay = canonicalDate(comprobante?.fecha_deposito);
      if (isISODate(depositDay)) values.add(depositDay);
    }
  });
  return Array.from(values);
};

const filterComprobantesByDay = (rows, dayValue) => {
  const selectedDay = canonicalDate(dayValue);
  if (!isISODate(selectedDay)) return [];

  return (Array.isArray(rows) ? rows : []).map((comprobante) => {
    const tramites = Array.isArray(comprobante?.tramites) ? comprobante.tramites : [];
    if (tramites.length > 0) {
      const tramitesDelDia = tramites.filter(
        (tramite) => canonicalDate(tramite?.fecha_documento) === selectedDay
      );
      if (tramitesDelDia.length === 0) return null;
      return {
        ...comprobante,
        tramites: tramitesDelDia,
      };
    }

    const depositDay = canonicalDate(comprobante?.fecha_deposito);
    if (depositDay !== selectedDay) return null;
    return comprobante;
  }).filter(Boolean);
};

const getDownloadFilename = (contentDisposition, fallback) => {
  const header = String(contentDisposition || '');
  const encodedMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim());
    } catch (error) {
      return encodedMatch[1].trim();
    }
  }

  const filenameMatch = header.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1]?.trim() || fallback;
};

const ReceiptQrEvidence = ({ receipt, onViewFile, isViewerOpen, selectedFile }) => {
  const check = currentQrCheck(receipt);
  const isManualReview = check?.status === 'manual_review_required' || check?.manual_review_required;
  const bankKind = check?.bank_kind || receipt?.bank_kind || 'legacy';
  const isOtherBank = bankKind === 'other';
  const bankName = check?.bank_name || receipt?.bank_name || '';
  const mismatches = Array.isArray(check?.mismatches) ? check.mismatches : [];
  const isUnreadablePichincha = bankKind === 'pichincha' && isManualReview && mismatches.includes('qr_unreadable');
  const statusLabel = displayQrStatus(receipt);

  return (
    <section className="rounded border border-white/20 bg-black/20 p-4" aria-label={`Comprobante ${receipt?.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="font-semibold text-white">Comprobante #{receipt?.id}</h5>
        <span className={clsx(
          'rounded px-2 py-1 text-sm font-semibold',
          isManualReview ? 'bg-amber-500 text-black' : 'bg-emerald-700 text-white'
        )}>
          {statusLabel}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <p>
          <span className="font-medium text-gray-300">Banco:</span>{' '}
          <span>{isOtherBank ? 'Otro banco' : bankKind === 'pichincha' ? 'Banco Pichincha' : 'Registro histórico'}</span>
        </p>
        <p>
          <span className="font-medium text-gray-300">Nombre del banco:</span>{' '}
          <span>{bankName || (bankKind === 'pichincha' ? 'Banco Pichincha' : 'No disponible')}</span>
        </p>
        <p><span className="font-medium text-gray-300">Tipo de pago:</span> {receipt?.tipo_pago || '-'}</p>
        <p><span className="font-medium text-gray-300">Archivo:</span>{' '}
          <button
            type="button"
            onClick={() => onViewFile(receipt?.archivo, String(receipt?.archivo || '').toLowerCase().endsWith('.pdf'))}
            className="text-notaria-yellow hover:underline focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
            aria-label={`Ver comprobante ${receipt?.id}`}
          >
            {isViewerOpen && selectedFile === receipt?.archivo ? 'Ocultar Comprobante' : 'Ver Comprobante'}
          </button>
        </p>
        <p><span className="font-medium text-gray-300">{isOtherBank ? 'Número de transacción:' : 'Número manual:'}</span> {check?.manual_number || receipt?.numero_transaccion || '-'}</p>
        <p><span className="font-medium text-gray-300">Número extraído:</span> {check?.extracted_number || 'No disponible'}</p>
        <p><span className="font-medium text-gray-300">{isOtherBank ? 'Fecha declarada:' : 'Fecha manual:'}</span> {check?.manual_date || receipt?.fecha_deposito || '-'}</p>
        <p><span className="font-medium text-gray-300">Fecha extraída:</span> {check?.extracted_date || 'No disponible'}</p>
        <p>
          <span className="font-medium text-gray-300">{isOtherBank ? 'Monto declarado:' : 'Monto manual:'}</span>{' '}
          <span>${Number(check?.manual_amount ?? receipt?.monto_total ?? 0).toFixed(2)}</span>
        </p>
        <p><span className="font-medium text-gray-300">Monto extraído:</span> {check?.extracted_amount == null ? 'No disponible' : `$${Number(check.extracted_amount).toFixed(2)}`}</p>
        <p><span className="font-medium text-gray-300">{isUnreadablePichincha ? 'Titular declarado de la cuenta:' : 'Titular de la cuenta QR:'}</span> {check?.account_owner || 'No disponible'}</p>
      </div>

      {check?.status === 'qr_fields_matched' && (
        <p role="status" className="mt-3 border-l-4 border-emerald-300 bg-emerald-950/60 p-3 text-sm text-emerald-50">
          {qrMatchedNotice}
        </p>
      )}

      {mismatches.length > 0 && (
        <p className="mt-3 text-sm text-amber-100">
          <span className="font-semibold">Diferencias:</span>{' '}
          {mismatches.map((value) => qrMismatchLabels[value] || value).join(', ')}
        </p>
      )}

      {isManualReview && (
        <div className="mt-3 border-l-4 border-amber-300 bg-amber-950/60 p-3 text-sm text-amber-50">
          {isUnreadablePichincha && (
            <p role="alert" className="mb-2 font-semibold">
              QR no verificado: pendiente de revisión manual. Datos declarados, sin preaprobación automática.
            </p>
          )}
          <p><span className="font-semibold">Motivo:</span> {check?.override_reason || 'No informado'}</p>
          <p><span className="font-semibold">{isOtherBank ? 'Registrado por:' : 'Actor del override:'}</span> {check?.override_actor_username || 'No informado'}</p>
          <p><span className="font-semibold">{isOtherBank ? 'Registrado el:' : 'Fecha del override:'}</span> {check?.created_at || 'No informada'}</p>
        </div>
      )}
    </section>
  );
};

const ApproveDeposits = () => {
  const money = useCallback((v) => {
    return formatMoneyCents(parseMoneyToCents(v) ?? 0n);
  }, []);

  
  const resolveFileUrl = useCallback((fileUrl) => {
    if (!fileUrl) return fileUrl;
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;

    const apiBase = (api && api.defaults && typeof api.defaults.baseURL === 'string') ? api.defaults.baseURL : '';
    const base = /^https?:\/\//i.test(apiBase) ? apiBase : window.location.origin;

    try {
      return new URL(fileUrl, base).toString();
    } catch (e) {
      return fileUrl;
    }
  }, []);

  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth() || { user: null, loading: true };
  const [comprobantes, setComprobantes] = useState([]);
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const sortedDays = useMemo(() => {
    return [...(days || [])].sort((a, b) => String(b).localeCompare(String(a)));
  }, [days]);
  const [isLoadingFetch, setIsLoadingFetch] = useState(false);
  const [isLoadingApprove, setIsLoadingApprove] = useState(false);
  const [error, setError] = useState(null);
  const [expandedMatrizador, setExpandedMatrizador] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [processingComprobante, setProcessingComprobante] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [fileError, setFileError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('Todos'); // Nuevo estado para el filtro
  const [initialAuditRange] = useState(getDefaultAuditDateRange);
  const [dateFrom, setDateFrom] = useState(initialAuditRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialAuditRange.dateTo);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingAccounting, setIsExportingAccounting] = useState(false);
  const activeFetchRef = useRef(0);

  // Cargar los días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      let baseDays = [];
      try {
        const daysResponse = await api.get('/api/audit/document-days/', {
          params: { page: 1, page_size: 250 },
        });
        baseDays = Array.isArray(daysResponse?.data?.days) ? daysResponse.data.days : [];
        const normalizedBaseDays = Array.from(
          new Set(
            baseDays
              .map((d) => String(d || '').trim())
              .filter((d) => isISODate(d))
          )
        ).sort((a, b) => b.localeCompare(a));

        setDays(normalizedBaseDays);
        setSelectedDay((prev) => {
          if (prev && normalizedBaseDays.includes(prev)) return prev;
          return normalizedBaseDays[0] || prev || '';
        });
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
        return;
      }

      // Enriquecimiento opcional: si falla, no bloquea el selector de días.
      try {
        const comprobantesResponse = await api.get('/api/audit/comprobantes/approval-queue/', {
          params: { _t: Date.now() },
        });
        const pendingDays = Array.from(
          new Set(
            extractDaysFromComprobantes(extractComprobantesList(comprobantesResponse?.data))
              .map((d) => String(d || '').trim())
              .filter((d) => isISODate(d))
          )
        ).sort((a, b) => b.localeCompare(a));
        if (pendingDays.length === 0) return;

        setDays(pendingDays);
        setSelectedDay(pendingDays[0]);
      } catch (error) {
        // Silencioso: la fuente principal ya cargó.
      }
    };
    if (!authLoading && user?.is_superuser) {
      fetchDays();
    }
  }, [user, authLoading]);

  // Validar permisos
  useEffect(() => {
    if (!authLoading && !user?.is_superuser) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Función para cargar comprobantes manualmente
  const fetchComprobantes = useCallback(async ({ showValidationToast = true } = {}) => {
    const dayValue = canonicalDate(selectedDay);
    if (!dayValue) {
      if (showValidationToast) {
        toast.error('Por favor, selecciona un día.', { position: 'top-right', autoClose: 3000 });
      }
      return;
    }
    if (!isISODate(dayValue)) {
      if (showValidationToast) {
        toast.error('Fecha inválida. Usa formato YYYY-MM-DD.', { position: 'top-right', autoClose: 3000 });
      }
      return;
    }
    const requestId = activeFetchRef.current + 1;
    activeFetchRef.current = requestId;
    try {
      setIsLoadingFetch(true);
      setError(null);
      const response = await api.get('/api/audit/comprobantes/approval-queue/', {
        params: { fecha_documento: dayValue, _t: Date.now() },
      });
      if (requestId !== activeFetchRef.current) return;

      const normalizedList = filterComprobantesByDay(extractComprobantesList(response.data), dayValue);

      setComprobantes(normalizedList);
      if (normalizedList.length === 0 && showValidationToast) {
        toast.info('No hay comprobantes para el día seleccionado.', { position: 'top-right', autoClose: 3000 });
      }
    } catch (err) {
      if (err.response) {
        setError('Error del servidor al cargar los comprobantes. Inténtalo de nuevo más tarde.');
      } else if (err.request) {
        setError('No se pudo conectar al servidor. Verifica tu conexión a internet.');
      } else {
        setError('Error al cargar los comprobantes: ' + err.message);
      }
    } finally {
      if (requestId === activeFetchRef.current) {
        setIsLoadingFetch(false);
      }
    }
  }, [selectedDay]);

  useEffect(() => {
    if (!authLoading && user?.is_superuser && isISODate(selectedDay)) {
      fetchComprobantes({ showValidationToast: false });
    }
  }, [authLoading, user, selectedDay, fetchComprobantes]);

  const handleAuditExport = async () => {
    if (!isISODate(dateFrom) || !isISODate(dateTo) || dateFrom > dateTo) {
      toast.error('Selecciona un rango de fechas válido.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    setIsExporting(true);
    try {
      const response = await api.get('/api/audit/reports/deposit-approval-audit/', {
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: 'blob',
      });
      const contentDisposition = response?.headers?.['content-disposition']
        || response?.headers?.get?.('content-disposition');
      const filename = getDownloadFilename(
        contentDisposition,
        `auditoria-aprobacion-depositos-${dateFrom}-${dateTo}.xlsx`
      );
      const downloadUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (exportError) {
      toast.error(
        exportError?.response?.data?.error || 'No se pudo exportar la auditoría de depósitos.',
        { position: 'top-right', autoClose: 5000 }
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleAccountingExport = async () => {
    if (!isISODate(dateFrom) || !isISODate(dateTo) || dateFrom > dateTo) {
      toast.error('Selecciona un rango de fechas válido.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    setIsExportingAccounting(true);
    try {
      const response = await api.get('/api/audit/reports/approved-deposits-accounting/', {
        params: { period: 'range', date_from: dateFrom, date_to: dateTo },
        responseType: 'blob',
      });
      const contentDisposition = response?.headers?.['content-disposition']
        || response?.headers?.get?.('content-disposition');
      const filename = getDownloadFilename(
        contentDisposition,
        `informe-contable-depositos-${dateFrom}-${dateTo}.xlsx`
      );
      const downloadUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(downloadUrl);
      toast.success('Informe contable generado con las miniaturas.', {
        position: 'top-right', autoClose: 3000,
      });
    } catch (exportError) {
      toast.error(
        exportError?.response?.data?.error || 'No se pudo generar el informe contable.',
        { position: 'top-right', autoClose: 5000 }
      );
    } finally {
      setIsExportingAccounting(false);
    }
  };

  // Función para determinar el estado de un comprobante
  const getComprobanteStatus = (comprobante) => {
    if (comprobante.aprobado === true) {
      return { status: 'Aprobado', color: 'bg-green-500' };
    } else if (comprobante.aprobado === false && comprobante.notes) {
      return { status: 'Rechazado', color: 'bg-red-500' };
    }
    return { status: 'Pendiente', color: 'bg-yellow-500' };
  };

  // Agrupar comprobantes por matrizador y calcular estados
  const groupByMatrizador = useMemo(() => {
    const sourceComprobantes = Array.isArray(comprobantes) ? comprobantes : [];
    const grouped = sourceComprobantes.reduce((acc, comprobante) => {
      const statusInfo = getComprobanteStatus(comprobante);
      // Filtrar comprobantes según el estado seleccionado
      if (filterStatus !== 'Todos' && statusInfo.status !== filterStatus) {
        return acc;
      }

      const processedTramiteIds = new Set(); // Rastrear IDs de trámites únicos por comprobante
      const tramites = Array.isArray(comprobante?.tramites) ? comprobante.tramites : [];
      const fallbackLabel = (comprobante?.usuario_matrizador || 'Sin Matrizador').trim() || 'Sin Matrizador';
      const fallbackKey = getMatrizadorKey(fallbackLabel);

      if (!tramites.length) {
        if (!acc[fallbackKey]) {
          acc[fallbackKey] = {
            displayName: fallbackLabel,
            comprobantes: [],
            tramites: [],
          };
        }
        if (!acc[fallbackKey].comprobantes.some((c) => c.id === comprobante?.id)) {
          acc[fallbackKey].comprobantes.push({
            ...comprobante,
            status: statusInfo.status,
            statusColor: statusInfo.color,
          });
        }
        return acc;
      }

      const familyMatrizadores = new Map();
      tramites.forEach((tramite) => {
        const label = (tramite?.usuario_matrizador || fallbackLabel).trim() || 'Sin Matrizador';
        familyMatrizadores.set(getMatrizadorKey(label), label);
      });
      if (familyMatrizadores.size > 1) {
        const matrizadorEntries = Array.from(familyMatrizadores.entries());
        const familyKey = `FAMILIA-${comprobante?.id}-${matrizadorEntries.map(([key]) => key).join('|')}`;
        acc[familyKey] = {
          displayName: matrizadorEntries.map(([, label]) => label).join(' / '),
          comprobantes: [{
            ...comprobante,
            status: statusInfo.status,
            statusColor: statusInfo.color,
          }],
          tramites: tramites.map((tramite) => ({ ...tramite })),
        };
        return acc;
      }

      tramites.forEach((tramite) => {
        const matrizadorLabel = (tramite?.usuario_matrizador || fallbackLabel).trim() || 'Sin Matrizador';
        const matrizadorKey = getMatrizadorKey(matrizadorLabel);

        if (!acc[matrizadorKey]) {
          acc[matrizadorKey] = {
            displayName: matrizadorLabel,
            comprobantes: [],
            tramites: [],
          };
        }

        if (!acc[matrizadorKey].displayName || acc[matrizadorKey].displayName === 'Sin Matrizador') {
          acc[matrizadorKey].displayName = matrizadorLabel;
        }

        // Solo añadir el trámite si no ha sido procesado en este comprobante
        if (!processedTramiteIds.has(tramite?.id)) {
          processedTramiteIds.add(tramite?.id);
          if (!acc[matrizadorKey].tramites.some((t) => t.id === tramite?.id)) {
            acc[matrizadorKey].tramites.push({ ...tramite });
          }
        }
        if (!acc[matrizadorKey].comprobantes.some((c) => c.id === comprobante?.id)) {
          acc[matrizadorKey].comprobantes.push({
            ...comprobante,
            status: statusInfo.status,
            statusColor: statusInfo.color,
          });
        }
      });
      return acc;
    }, {});

    Object.keys(grouped).forEach((matrizadorKey) => {
      const { tramites, comprobantes: comprobantesByMatrizador } = grouped[matrizadorKey];
      const totalAjustadoCents = tramites.length
        ? tramites.reduce((sum, tramite) => sum + getTramiteAmountCents(tramite), 0n)
        : comprobantesByMatrizador.reduce(
          (sum, comprobante) => sum + (parseMoneyToCents(comprobante?.monto_total) ?? 0n),
          0n
        );
      const seenReceiptIds = new Set();
      const familyReceipts = comprobantesByMatrizador.flatMap((comprobante) => receiptFamily(comprobante));
      let hasIndividualReceiptAmounts = true;
      const receiptTotal = familyReceipts.reduce((sum, receipt) => {
        const receiptKey = receipt?.id ?? `${receipt?.numero_transaccion}-${receipt?.fecha_deposito}`;
        if (seenReceiptIds.has(receiptKey)) return sum;
        seenReceiptIds.add(receiptKey);
        const amount = individualReceiptAmount(receipt);
        if (amount === null) {
          hasIndividualReceiptAmounts = false;
          return sum;
        }
        return sum + amount;
      }, 0n);
      const totalComprobantesCents = hasIndividualReceiptAmounts ? receiptTotal : null;
      const differenceCents = hasIndividualReceiptAmounts
        ? totalAjustadoCents - totalComprobantesCents
        : null;

      // Determinar el estado general del matrizador
      const allAprobados = comprobantesByMatrizador.every((c) => c.status === 'Aprobado');
      const allRechazados = comprobantesByMatrizador.every((c) => c.status === 'Rechazado');
      const allFinalizados = comprobantesByMatrizador.every((c) => c.status === 'Aprobado' || c.status === 'Rechazado');
      let matrizadorStatus = '';
      let matrizadorStatusColor = '';
      let accountingStatus = '';
      let accountingStatusColor = '';

      const isDifferenceZero = hasIndividualReceiptAmounts && differenceCents === 0n;
      if (!hasIndividualReceiptAmounts) {
        accountingStatus = 'No disponible';
        accountingStatusColor = 'bg-gray-500';
      } else if (isDifferenceZero) {
        accountingStatus = 'Cuadrado';
        accountingStatusColor = 'bg-green-500';
      } else if (comprobantesByMatrizador.length === 0) {
        accountingStatus = 'Pendiente';
        accountingStatusColor = 'bg-yellow-500';
      } else {
        accountingStatus = `Falta $${formatMoneyCents(differenceCents)}`;
        accountingStatusColor = 'bg-red-500';
      }

      // Calcular el estado de aprobación/rechazo
      if (isDifferenceZero && allAprobados) {
        matrizadorStatus = `Cuadrado (${comprobantesByMatrizador.length}/${comprobantesByMatrizador.length})`;
        matrizadorStatusColor = 'bg-green-500';
      } else if (allAprobados) {
        matrizadorStatus = `Todos Aprobados (${comprobantesByMatrizador.length}/${comprobantesByMatrizador.length})`;
        matrizadorStatusColor = 'bg-green-500';
      } else if (allRechazados) {
        matrizadorStatus = `Todos Rechazados (${comprobantesByMatrizador.length}/${comprobantesByMatrizador.length})`;
        matrizadorStatusColor = 'bg-red-500';
      } else if (allFinalizados) {
        const aprobadosCount = comprobantesByMatrizador.filter((c) => c.status === 'Aprobado').length;
        matrizadorStatus = `Finalizados: ${aprobadosCount} Aprobados, ${comprobantesByMatrizador.length - aprobadosCount} Rechazados`;
        matrizadorStatusColor = 'bg-gray-500';
      } else {
        const pendientesCount = comprobantesByMatrizador.filter((c) => c.status === 'Pendiente').length;
        matrizadorStatus = `Pendientes: ${pendientesCount}/${comprobantesByMatrizador.length}`;
        matrizadorStatusColor = 'bg-yellow-500';
      }

      grouped[matrizadorKey].totalAjustado = formatMoneyCents(totalAjustadoCents);
      grouped[matrizadorKey].totalComprobantes = totalComprobantesCents === null
        ? null
        : formatMoneyCents(totalComprobantesCents);
      grouped[matrizadorKey].difference = differenceCents === null
        ? null
        : formatMoneyCents(differenceCents);
      grouped[matrizadorKey].hasIndividualReceiptAmounts = hasIndividualReceiptAmounts;
      grouped[matrizadorKey].receiptCount = seenReceiptIds.size;
      grouped[matrizadorKey].status = accountingStatus;
      grouped[matrizadorKey].statusColor = accountingStatusColor;
      grouped[matrizadorKey].matrizadorStatus = matrizadorStatus;
      grouped[matrizadorKey].matrizadorStatusColor = matrizadorStatusColor;
      grouped[matrizadorKey].allFinalizados = allFinalizados;
    });

    return grouped;
  }, [comprobantes, filterStatus]);

  // Aprobar un comprobante
  const handleApproveComprobante = async (comprobanteId, matrizador, isBulk = false) => {
    setProcessingComprobante(isBulk ? null : comprobanteId);
    setIsLoadingApprove(isBulk);
    try {
      await api.post('/api/audit/approve-comprobante/', {
        comprobante_id: comprobanteId,
      });
      const matrizadorLabel = groupByMatrizador[matrizador]?.displayName || matrizador;
      setActionFeedback({
        type: 'success',
        message: isBulk 
          ? `Comprobantes de ${matrizadorLabel} aprobados exitosamente. (${groupByMatrizador[matrizador]?.tramites?.length || 0} trámites afectados)`
          : `Comprobante #${comprobanteId} aprobado exitosamente.`,
      });
      setTimeout(() => setActionFeedback(null), 2000);
      await fetchComprobantes();
    } catch (error) {
      const message = error.response?.data?.error || 'Error al aprobar el comprobante.';
      setActionFeedback({
        type: 'error',
        message: message,
      });
      setTimeout(() => setActionFeedback(null), 3000);
      if (error.response?.status === 409 || error.response?.data?.code === 'stale_review') {
        await fetchComprobantes({ showValidationToast: false });
      }
    } finally {
      setProcessingComprobante(null);
      setIsLoadingApprove(false);
    }
  };

  // Rechazar un comprobante
  const handleRejectComprobante = async (comprobanteId, matrizador, notes, isBulk = false) => {
    setProcessingComprobante(isBulk ? null : comprobanteId);
    setIsLoadingApprove(isBulk);
    try {
      await api.post('/api/audit/reject-comprobante/', {
        comprobante_id: comprobanteId,
        notes: notes,
      });
      const matrizadorLabel = groupByMatrizador[matrizador]?.displayName || matrizador;
      setActionFeedback({
        type: 'success',
        message: isBulk 
          ? `Comprobantes de ${matrizadorLabel} rechazados exitosamente. (${groupByMatrizador[matrizador]?.tramites?.length || 0} trámites afectados)`
          : `Comprobante #${comprobanteId} rechazado exitosamente.`,
      });
      setTimeout(() => setActionFeedback(null), 2000);
      await fetchComprobantes();
    } catch (error) {
      const message = error.response?.data?.error || 'Error al rechazar el comprobante.';
      setActionFeedback({
        type: 'error',
        message: message,
      });
      setTimeout(() => setActionFeedback(null), 3000);
      if (error.response?.status === 409 || error.response?.data?.code === 'stale_review') {
        await fetchComprobantes({ showValidationToast: false });
      }
    } finally {
      setProcessingComprobante(null);
      setIsLoadingApprove(false);
      setConfirmAction(null);
      setRejectionNotes('');
    }
  };

  // Aprobar todos los comprobantes de un matrizador
  const handleApproveMatrizador = (matrizador) => {
    setConfirmAction({ type: 'approve', matrizador });
  };

  // Rechazar todos los comprobantes de un matrizador
  const handleRejectMatrizador = (matrizador) => {
    setConfirmAction({ type: 'reject', matrizador });
  };

  // Confirmar acción (aprobar o rechazar)
  const handleConfirmAction = () => {
    const { type, matrizador, comprobanteId } = confirmAction;
    const rejectionReason = rejectionNotes.trim();
    if (type === 'reject' && !rejectionReason) return;
    if (comprobanteId) {
      if (type === 'approve') {
        handleApproveComprobante(comprobanteId, matrizador);
      } else {
        handleRejectComprobante(comprobanteId, matrizador, rejectionReason);
      }
    } else {
      const comprobantesToProcess = (groupByMatrizador[matrizador]?.comprobantes || []).filter((c) => c.status === 'Pendiente');
      if (type === 'approve') {
        comprobantesToProcess.forEach((comprobante) => {
          handleApproveComprobante(comprobante.id, matrizador, true);
        });
      } else {
        comprobantesToProcess.forEach((comprobante) => {
          handleRejectComprobante(comprobante.id, matrizador, rejectionReason, true);
        });
      }
    }
  };

  // Ver archivo (PDF o imagen)
  const handleViewFile = (fileUrl, isPdf) => {
    if (isPdf) {
      window.open(resolveFileUrl(fileUrl), '_blank');
    } else {
      setSelectedFile(fileUrl);
      setShowFileViewer(true);
      setZoomLevel(1);
      setFileError(null);
    }
  };

  // Zoom in
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 3));
  };

  // Zoom out
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  // Manejo de error al cargar imagen
  const onImageLoadError = () => {
    setFileError('No se pudo cargar la imagen. Intenta descargarla directamente.');
    toast.error('Error al cargar la imagen.', { position: 'top-right' });
  };

  // Manejo de cambio de día
  const handleDayChange = (e) => {
    activeFetchRef.current += 1; // invalida respuestas tardías del día anterior
    setSelectedDay(e.target.value);
    setComprobantes([]);
    setExpandedMatrizador(null);
    setFilterStatus('Todos'); // Reiniciar el filtro al cambiar de día
    setError(null);
  };

  // Manejo de cambio de filtro
  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setExpandedMatrizador(null); // Contraer todos los matrizadores al cambiar el filtro
  };

  if (authLoading) {
    return <div className="text-white text-center">Cargando...</div>;
  }

  if (!user?.is_superuser) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Aprobar Depósitos</h1>
        </div>
        <Link
          to="/dashboard/notario"
          className="bg-notaria-yellow text-notaria-blue-1 font-bold py-1.5 px-3 sm:py-2 sm:px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all duration-300"
          aria-label="Volver al Dashboard"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        <section className="max-w-5xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4">Exportar auditoría de aprobaciones</h2>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div>
              <label htmlFor="audit_date_from" className="block text-sm sm:text-base font-semibold mb-2">
                Desde
              </label>
              <input
                id="audit_date_from"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              />
            </div>
            <div>
              <label htmlFor="audit_date_to" className="block text-sm sm:text-base font-semibold mb-2">
                Hasta
              </label>
              <input
                id="audit_date_to"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              />
            </div>
            <motion.button
              type="button"
              onClick={handleAuditExport}
              disabled={isExporting}
              className={clsx(
                'w-full sm:w-auto min-h-[44px] bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg',
                'hover:bg-notaria-red hover:text-white transition-all flex items-center justify-center',
                isExporting && 'opacity-50 cursor-not-allowed'
              )}
              whileHover={{ scale: isExporting ? 1 : 1.02 }}
              whileTap={{ scale: isExporting ? 1 : 0.98 }}
            >
              {isExporting ? (
                <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
              ) : (
                <DocumentArrowDownIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              )}
              {isExporting ? 'Exportando...' : 'Exportar auditoría'}
            </motion.button>
            <motion.button
              type="button"
              onClick={handleAccountingExport}
              disabled={isExportingAccounting}
              title="Excel de los comprobantes aprobados con todos los datos y la miniatura"
              className={clsx(
                'w-full sm:w-auto min-h-[44px] bg-notaria-blue-3 text-white font-bold py-2 px-4 rounded-lg',
                'hover:bg-notaria-blue-2 transition-all flex items-center justify-center',
                isExportingAccounting && 'opacity-50 cursor-not-allowed'
              )}
              whileHover={{ scale: isExportingAccounting ? 1 : 1.02 }}
              whileTap={{ scale: isExportingAccounting ? 1 : 0.98 }}
            >
              {isExportingAccounting ? (
                <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
              ) : (
                <DocumentArrowDownIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              )}
              {isExportingAccounting ? 'Generando...' : 'Informe contable XLSX'}
            </motion.button>
          </div>
        </section>

        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Seleccionar Día y Filtro</h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
            <div className="w-full sm:w-1/3">
              <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                Día de los Comprobantes
              </label>
              <div className="space-y-2">
                <select
                  id="fecha_documento"
                  value={selectedDay}
                  onChange={handleDayChange}
                  className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                >
                  <option value="">Seleccione día</option>
                  {sortedDays.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
                {sortedDays.length === 0 ? (
                  <p className="text-xs sm:text-sm text-notaria-red">
                    No hay días con comprobantes disponibles.
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-gray-300">
                    Selecciona un día de la lista para ver los trámites por aprobar.
                  </p>
                )}
              </div>
            </div>
            <div className="w-full sm:w-1/3">
              <label htmlFor="filter_status" className="block text-sm sm:text-lg font-semibold mb-2">
                Filtrar por Estado
              </label>
              <select
                id="filter_status"
                value={filterStatus}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              >
                <option value="Todos">Todos</option>
                <option value="Pendiente">Pendientes</option>
                <option value="Aprobado">Aprobados</option>
                <option value="Rechazado">Rechazados</option>
              </select>
            </div>
            <motion.button
              onClick={() => fetchComprobantes({ showValidationToast: true })}
              disabled={isLoadingFetch || !selectedDay}
              className={clsx(
                'bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg',
                'hover:bg-notaria-red hover:text-white transition-all',
                isLoadingFetch || !selectedDay ? 'opacity-50 cursor-not-allowed' : ''
              )}
              whileHover={{ scale: isLoadingFetch || !selectedDay ? 1 : 1.02 }}
              whileTap={{ scale: isLoadingFetch || !selectedDay ? 1 : 0.98 }}
            >
              {isLoadingFetch ? (
                <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
              ) : (
                'Cargar Comprobantes'
              )}
            </motion.button>
          </div>
        </motion.div>

        {isLoadingFetch ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-200"
          >
            Cargando comprobantes, por favor espera...
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-5xl mx-auto bg-red-500 text-white p-4 rounded-lg mb-6 sm:mb-8"
          >
            {error}
          </motion.div>
        ) : (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-6 sm:mb-8 text-gray-200">Historial de Depósitos por Matrizador</h2>
            {actionFeedback && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className={`p-4 rounded-lg mb-4 ${actionFeedback.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white flex items-center justify-center`}
                aria-live="polite"
              >
                {actionFeedback.message}
              </motion.div>
            )}
            {Object.keys(groupByMatrizador).length === 0 ? (
              <p className="text-center text-lg text-gray-400">
                {filterStatus === 'Todos'
                  ? 'No hay comprobantes para el día seleccionado.'
                  : `No hay comprobantes con estado "${filterStatus}" para el día seleccionado.`}
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupByMatrizador).map(([matrizadorKey, data]) => (
                  <motion.div
                    key={matrizadorKey}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-notaria-blue-1 bg-opacity-80 rounded-xl shadow-lg overflow-hidden"
                  >
                    <button
                      className="flex justify-between items-center p-4 sm:p-6 w-full text-left cursor-pointer"
                      onClick={() => setExpandedMatrizador(expandedMatrizador === matrizadorKey ? null : matrizadorKey)}
                      aria-expanded={expandedMatrizador === matrizadorKey}
                      aria-controls={`matrizador-${matrizadorKey}`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className={`w-4 h-4 rounded-full ${data.statusColor}`} aria-hidden="true"></span>
                        <h3 className="text-lg sm:text-xl font-semibold">{data.displayName || matrizadorKey}</h3>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={clsx(
                          'text-sm sm:text-base font-medium',
                          {
                            'text-green-300': data.statusColor.includes('green'),
                            'text-yellow-300': data.statusColor.includes('yellow'),
                            'text-red-300': data.statusColor.includes('red'),
                          }
                        )}>
                          {data.status}
                        </span>
                        <span className={clsx(
                          'text-sm sm:text-base font-medium px-2 py-1 rounded',
                          data.matrizadorStatusColor,
                          'text-white'
                        )}>
                          {data.matrizadorStatus}
                        </span>
                        {expandedMatrizador === matrizadorKey ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-300" aria-hidden="true" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-300" aria-hidden="true" />
                        )}
                      </div>
                    </button>
                    <AnimatePresence>
                      {expandedMatrizador === matrizadorKey && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="p-4 sm:p-6 bg-notaria-blue-2 bg-opacity-50"
                          id={`matrizador-${matrizadorKey}`}
                        >
                          <div className="mb-6">
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-gray-200">Resumen</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm sm:text-base">
                              <p><span className="font-medium text-gray-300">Total de trámites:</span> ${money(data.totalAjustado)}</p>
                              {data.hasIndividualReceiptAmounts ? (
                                <>
                                  <p><span className="font-medium text-gray-300">Suma individual de comprobantes:</span> ${money(data.totalComprobantes)}</p>
                                  <p><span className="font-medium text-gray-300">Diferencia:</span> ${money(data.difference)}</p>
                                </>
                              ) : (
                                <p className="sm:col-span-2">No disponible para comprobantes anteriores a la validación QR</p>
                              )}
                              <p><span className="font-medium text-gray-300">Trámites:</span> {data.tramites.length}</p>
                              <p><span className="font-medium text-gray-300">Comprobantes:</span> {data.receiptCount}</p>
                            </div>
                          </div>

                          <div className="mb-6">
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-gray-200">Comprobantes</h4>
                            {data.comprobantes.length === 0 ? (
                              <p className="text-sm sm:text-base text-gray-400">No hay comprobantes asociados.</p>
                            ) : (
                              <div className="space-y-3">
                                {data.comprobantes.map(comprobante => (
                                  <div key={comprobante.id} className="bg-notaria-blue-1 bg-opacity-70 p-4 rounded-lg">
                                    <div className="flex justify-between items-center mb-2">
                                      <span className={clsx(
                                        'text-sm font-medium px-2 py-1 rounded',
                                        comprobante.statusColor,
                                        'text-white'
                                      )}>
                                        {comprobante.status}
                                      </span>
                                    </div>
                                    <div className="space-y-3">
                                      {receiptFamily(comprobante).map((receipt) => (
                                        <ReceiptQrEvidence
                                          key={receipt?.id}
                                          receipt={receipt}
                                          onViewFile={handleViewFile}
                                          isViewerOpen={showFileViewer}
                                          selectedFile={selectedFile}
                                        />
                                      ))}
                                    </div>
                                    {showFileViewer && selectedFile && !String(selectedFile).toLowerCase().endsWith('.pdf') && (
                                      <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.3 }}
                                        className="mt-4 bg-white rounded-lg p-4"
                                      >
                                        <div className="flex justify-between items-center mb-2">
                                          <h4 className="text-base font-semibold text-black">Vista del Comprobante</h4>
                                          <div className="flex items-center space-x-2">
                                            <motion.button
                                              onClick={handleZoomOut}
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.9 }}
                                              aria-label="Reducir zoom"
                                            >
                                              <MagnifyingGlassMinusIcon className="w-5 h-5" />
                                            </motion.button>
                                            <motion.button
                                              onClick={handleZoomIn}
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.9 }}
                                              aria-label="Aumentar zoom"
                                            >
                                              <MagnifyingGlassPlusIcon className="w-5 h-5" />
                                            </motion.button>
                                            <a
                                              href={resolveFileUrl(selectedFile)}
                                              download
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              aria-label="Descargar comprobante"
                                            >
                                              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                                <DocumentArrowDownIcon className="w-5 h-5" />
                                              </motion.div>
                                            </a>
                                            <motion.button
                                              onClick={() => setShowFileViewer(false)}
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.9 }}
                                              aria-label="Cerrar visor de archivo"
                                            >
                                              <XMarkIcon className="w-5 h-5" />
                                            </motion.button>
                                          </div>
                                        </div>
                                        {fileError ? (
                                          <div className="text-center text-red-600">
                                            <p>{fileError}</p>
                                            <a
                                              href={resolveFileUrl(selectedFile)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-notaria-yellow hover:underline mt-2 inline-block"
                                              aria-label="Abrir archivo en una nueva pestaña"
                                            >
                                              Abrir en una nueva pestaña
                                            </a>
                                          </div>
                                        ) : (
                                          <div className="flex justify-center">
                                            <img
                                              src={resolveFileUrl(selectedFile)}
                                              alt="Comprobante"
                                              className="max-w-full max-h-[50vh] object-contain"
                                              style={{ transform: `scale(${zoomLevel})`, transition: 'transform 0.3s ease' }}
                                              onError={onImageLoadError}
                                            />
                                          </div>
                                        )}
                                      </motion.div>
                                    )}
                                    {receiptFamily(comprobante).some((receipt) => (
                                      currentQrCheck(receipt)?.status === 'manual_review_required'
                                      || currentQrCheck(receipt)?.manual_review_required
                                    )) && (
                                      <div role="alert" className="mt-4 border-l-4 border-amber-300 bg-amber-950/80 p-4 font-semibold text-amber-50">
                                        Esta familia contiene una revisión manual con alerta. Revisa la evidencia antes de aprobar o rechazar.
                                      </div>
                                    )}
                                    {comprobante.status === 'Pendiente' && (
                                      <div className="mt-3 flex space-x-3">
                                        <motion.button
                                          onClick={() => handleApproveComprobante(comprobante.id, matrizadorKey)}
                                          disabled={processingComprobante === comprobante.id || isLoadingApprove}
                                          className={clsx(
                                            'bg-blue-500 text-white py-1 px-3 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all flex items-center',
                                            processingComprobante === comprobante.id || isLoadingApprove ? 'opacity-50 cursor-not-allowed' : ''
                                          )}
                                          whileHover={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 1.05 }}
                                          whileTap={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 0.95 }}
                                          aria-label={`Aprobar comprobante ${comprobante.id}`}
                                          aria-busy={processingComprobante === comprobante.id}
                                        >
                                          {processingComprobante === comprobante.id ? (
                                            <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                                          ) : (
                                            <CheckCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                                          )}
                                          Aprobar
                                        </motion.button>
                                        <motion.button
                                          onClick={() => setConfirmAction({ type: 'reject', matrizador: matrizadorKey, comprobanteId: comprobante.id })}
                                          disabled={processingComprobante === comprobante.id || isLoadingApprove}
                                          className={clsx(
                                            'bg-notaria-red text-white py-1 px-3 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all flex items-center',
                                            processingComprobante === comprobante.id || isLoadingApprove ? 'opacity-50 cursor-not-allowed' : ''
                                          )}
                                          whileHover={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 1.05 }}
                                          whileTap={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 0.95 }}
                                          aria-label={`Rechazar comprobante ${comprobante.id}`}
                                          aria-busy={processingComprobante === comprobante.id}
                                        >
                                          {processingComprobante === comprobante.id ? (
                                            <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                                          ) : (
                                            <ExclamationCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                                          )}
                                          Rechazar
                                        </motion.button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-gray-200">Trámites Asociados</h4>
                            <div className="max-w-full overflow-x-auto">
                              <table role="grid" aria-describedby={`tramites-table-description-${matrizadorKey}`} className="w-full border-collapse">
                                <caption id={`tramites-table-description-${matrizadorKey}`} className="sr-only">
                                  Lista de trámites asociados al matrizador {data.displayName || matrizadorKey} para el día {selectedDay}
                                </caption>
                                <thead>
                                  <tr className="bg-notaria-blue-1 text-white">
                                    <th className="p-2 text-left">No. Temporal</th>
                                    <th className="p-2 text-left">No. Comprobante</th>
                                    <th className="p-2 text-left">Fecha Documento</th>
                                    <th className="p-2 text-left">Objeto Escritura</th>
<th className="p-2 text-left">Total Ajustado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data.tramites.map((tramite) => (
                                    <tr key={tramite.id} className="border-b border-notaria-blue-2">
                                      <td className="p-2">{tramite.no_temporal || ''}</td>
                                      <td className="p-2">{tramite.no_comprobante || ''}</td>
                                      <td className="p-2">{tramite.fecha_documento || ''}</td>
                                      <td className="p-2">{tramite.acto || ''}</td>
                                      <td className="p-2">${formatMoneyCents(getTramiteAmountCents(tramite))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {data.status === 'Cuadrado' && !data.allFinalizados && (
                            <div className="mt-6 flex space-x-4">
                              <motion.button
                                onClick={() => handleApproveMatrizador(matrizadorKey)}
                                className={clsx(
                                  'w-full bg-blue-500 text-white font-bold py-2 rounded-lg',
                                  'hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-300',
                                  'flex items-center justify-center',
                                  {
                                    'opacity-50 cursor-not-allowed': isLoadingApprove,
                                  }
                                )}
                                whileHover={{ scale: isLoadingApprove ? 1 : 1.02 }}
                                whileTap={{ scale: isLoadingApprove ? 1 : 0.98 }}
                                disabled={isLoadingApprove}
                                aria-label={`Aprobar todos los depósitos de ${data.displayName || matrizadorKey}`}
                              >
                                <CheckCircleIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                                Aprobar Todos
                              </motion.button>
                              <motion.button
                                onClick={() => handleRejectMatrizador(matrizadorKey)}
                                className={clsx(
                                  'w-full bg-notaria-red text-white font-bold py-2 rounded-lg',
                                  'hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-300',
                                  'flex items-center justify-center',
                                  {
                                    'opacity-50 cursor-not-allowed': isLoadingApprove,
                                  }
                                )}
                                whileHover={{ scale: isLoadingApprove ? 1 : 1.02 }}
                                whileTap={{ scale: isLoadingApprove ? 1 : 0.98 }}
                                disabled={isLoadingApprove}
                                aria-label={`Rechazar todos los depósitos de ${data.displayName || matrizadorKey}`}
                              >
                                <ExclamationCircleIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                                Rechazar Todos
                              </motion.button>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </main>

      <AnimatePresence>
        {confirmAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-notaria-blue-1 bg-opacity-95 p-6 rounded-2xl shadow-2xl w-full max-w-md"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">
                  {confirmAction.type === 'approve' ? 'Aprobar' : 'Rechazar'} {confirmAction.comprobanteId ? `Comprobante #${confirmAction.comprobanteId}` : `Todos los Comprobantes de ${groupByMatrizador[confirmAction.matrizador]?.displayName || confirmAction.matrizador}`}
                </h3>
                <motion.button
                  onClick={() => setConfirmAction(null)}
                  className="text-gray-300 hover:text-notaria-yellow transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Cerrar modal"
                >
                  <XMarkIcon className="w-6 h-6" />
                </motion.button>
              </div>
              {confirmAction.type === 'reject' && (
                <div className="mb-4">
                  <label htmlFor="rejectionNotes" className="block text-sm font-semibold mb-1">
                    Motivo del Rechazo
                  </label>
                  <p id="rejectionNotesRequired" className="mb-1 text-sm text-red-200">Campo obligatorio</p>
                  <textarea
                    id="rejectionNotes"
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    rows="4"
                    placeholder="Escribe el motivo del rechazo aquí..."
                    aria-label="Motivo del rechazo"
                    aria-describedby="rejectionNotesRequired"
                    required
                  />
                </div>
              )}
              <div className="flex justify-end space-x-4">
                <motion.button
                  onClick={() => setConfirmAction(null)}
                  className="bg-notaria-blue-2 text-white py-2 px-4 rounded-lg hover:bg-notaria-blue-3 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Cancelar"
                >
                  Cancelar
                </motion.button>
                <motion.button
                  onClick={handleConfirmAction}
                  disabled={
                    isLoadingApprove
                    || processingComprobante
                    || (confirmAction.type === 'reject' && !rejectionNotes.trim())
                  }
                  className={clsx(
                    confirmAction.type === 'approve' ? 'bg-blue-500' : 'bg-notaria-red',
                    'text-white py-2 px-4 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all flex items-center',
                    isLoadingApprove || processingComprobante || (confirmAction.type === 'reject' && !rejectionNotes.trim())
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  )}
                  whileHover={{ scale: isLoadingApprove || processingComprobante || (confirmAction.type === 'reject' && !rejectionNotes.trim()) ? 1 : 1.05 }}
                  whileTap={{ scale: isLoadingApprove || processingComprobante || (confirmAction.type === 'reject' && !rejectionNotes.trim()) ? 1 : 0.95 }}
                  aria-label={confirmAction.type === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
                  aria-busy={isLoadingApprove || processingComprobante}
                >
                  {isLoadingApprove || processingComprobante ? (
                    <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                  ) : confirmAction.type === 'approve' ? (
                    <CheckCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                  ) : (
                    <ExclamationCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                  )}
                  {confirmAction.type === 'approve' ? 'Aprobar' : 'Rechazar'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default ApproveDeposits;
